import { environment } from '../environments/environment';

export interface FirebaseRuntimeConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface FrontendRuntimeConfig {
  RELEASE_NOTES_URL?: string;
  GOOGLE_MAPS_API_KEY: string;
  firebaseConfig: FirebaseRuntimeConfig;
  firebaseTenantId: string;
  firestoreDatabaseId: string;
  GoogleLoginProvider: string;
  FacebookLoginProvider: string;
  recaptchaSiteKey: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFirebaseRuntimeConfig(value: unknown): value is FirebaseRuntimeConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ].every(field => isNonEmptyString(candidate[field]));
}

function isFrontendRuntimeConfig(value: unknown): value is FrontendRuntimeConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isNonEmptyString(candidate['GOOGLE_MAPS_API_KEY'])
    && isFirebaseRuntimeConfig(candidate['firebaseConfig'])
    && isNonEmptyString(candidate['firebaseTenantId'])
    && isNonEmptyString(candidate['firestoreDatabaseId'])
    && candidate['firestoreDatabaseId'].trim() !== '(default)'
    && isNonEmptyString(candidate['GoogleLoginProvider'])
    && isNonEmptyString(candidate['FacebookLoginProvider'])
    && isNonEmptyString(candidate['recaptchaSiteKey']);
}

function isLocalDevelopmentHost(): boolean {
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '::1'
    || hostname === 'portal.smartview-ums.app'
    || hostname === 'portal.landis-ums.app'
    || hostname === 'portal.eykon-ums.app';
}

async function loadLocalRuntimeConfig(): Promise<FrontendRuntimeConfig> {
  const localResponse = await fetch('/assets/runtime-config.local.json', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  });

  if (!localResponse.ok) {
    throw new Error('Local runtime configuration is missing. Run `npm run setup:runtime-config` before `ng serve`.');
  }

  const localRuntimeConfig: unknown = await localResponse.json();
  if (!isFrontendRuntimeConfig(localRuntimeConfig)) {
    throw new Error('Local runtime configuration is incomplete');
  }

  return localRuntimeConfig;
}

export async function loadRuntimeConfig(): Promise<void> {
  let runtimeConfig: unknown;
  let shouldLoadLocalRuntimeConfig = false;
  const allowLocalRuntimeConfig = isLocalDevelopmentHost();

  try {
    const response = await fetch('/runtime-config', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) {
      // Angular CLI does not expose the Express endpoint and normally returns
      // a 404 for this path. Only that development-server response should use
      // the generated local asset. A Docker/Express 503 must remain visible so
      // missing Secret Manager values are not masked by an HTML fallback page.
      shouldLoadLocalRuntimeConfig =
        allowLocalRuntimeConfig && response.status === 404;
      if (!shouldLoadLocalRuntimeConfig) {
        throw new Error(`Runtime configuration request failed (${response.status})`);
      }
    }

    if (!shouldLoadLocalRuntimeConfig) {
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (!contentType.includes('application/json')) {
        // When Angular CLI falls back to index.html for an unknown route, it
        // returns HTML with status 200. Treat that as the local-dev signal.
        shouldLoadLocalRuntimeConfig =
          allowLocalRuntimeConfig && contentType.includes('text/html');
      }

      if (!shouldLoadLocalRuntimeConfig) {
        runtimeConfig = await response.json();
        if (!isFrontendRuntimeConfig(runtimeConfig)) {
          throw new Error('Runtime configuration response is incomplete');
        }
      }
    }
  } catch (runtimeEndpointError) {
    // Angular CLI does not run server/server.js. On loopback hosts only, allow
    // the ignored asset generated by scripts/setup/local-runtime-config.js,
    // including when testing an Angular configuration with production=true.
    if (!shouldLoadLocalRuntimeConfig) {
      throw runtimeEndpointError;
    }
  }

  if (shouldLoadLocalRuntimeConfig) {
    runtimeConfig = await loadLocalRuntimeConfig();
  }

  // Angular modules import the environment object directly. Mutating that
  // shared object before bootstrap keeps the existing consumers unchanged
  // while moving the values out of compiled environment files.
  Object.assign(environment, runtimeConfig);
}
